/* eslint-disable no-magic-numbers */
import { Sidebar } from '@futrli/ui-components';
import Typography from 'carbon-react/lib/components/typography';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';

import { Forecast } from '@futrli-sdk/predict';
import { ConsolidatedForecastUpdateRequest } from '@futrli-sdk/consolidations';
import { Logger } from '@futrli/shared-services';
import { LOADER_AREAS, toastError, UISystemIcon } from '@futrli/ui-components';

import { useFirstName } from '../../../../dashboard/hooks';
import * as styles from './checklist-popover.scss';
import { ChecklistItemIDs, useCheckboxInfos } from './useCheckboxInfos';
import { ASSISTANT_TAB } from '../../../../ui/assistant-side-panel/assistant-side-panel';
import { SidePanelContext } from '../../context/side-panel';

interface ForecastInfo {
  id: ChecklistItemIDs;
  label: ReactNode;
  link: string;
  shouldOpenSettings?: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  forecast: Forecast;
  updateForecast?: (
    forecast: Partial<ConsolidatedForecastUpdateRequest> | Partial<Forecast>,
    shouldRefetchReport?: boolean | undefined,
    loadingArea?: LOADER_AREAS
  ) => Promise<void>;
}

const BUDGET_ACTIONS_NUM = 4;
const SCENARIO_ACTIONS_NUM = 8;
const SCENARIO_WITH_INVOICES_ACTION_NUM = 9;

/**
 * Determines if save button is disabled
 * @param forecastChecklist list of checked steps from forecast object
 * @param stepsSelection Map of steps and their check value
 * @returns true if save button is disabled
 */
const isSaveDisabled = (forecastChecklist: string[], stepsSelection: Record<string, boolean>, isLoading: boolean) => {
  if (isLoading) {
    return true;
  }

  return (
    forecastChecklist.every(id => stepsSelection[id]) &&
    Object.entries(stepsSelection)
      .filter(([key]) => !forecastChecklist.includes(key))
      .every(([, value]) => !value)
  );
};

export const ChecklistDrawer = ({ isOpen, onClose, forecast, updateForecast }: Props) => {
  const intl = useIntl();
  const firstName = useFirstName();
  const checkboxInfos = useCheckboxInfos(forecast);
  const { openSidePanel } = useContext(SidePanelContext);

  const [stepsSelection, setStepsSelection] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const booleanObject: Record<string, boolean> = {};
    for (const item of checkboxInfos) {
      booleanObject[item.id] = false;
    }
    const { checklist } = forecast;
    for (const item of checklist) {
      booleanObject[item] = true;
    }

    setStepsSelection(booleanObject);
  }, [forecast, checkboxInfos]);

  const numberOfSelectedItems = useMemo(() => Object.values(stepsSelection).filter(value => value).length, [
    stepsSelection
  ]);

  const handleCheckboxClick = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, id: ChecklistItemIDs) => {
      const { checked } = event.target;
      setStepsSelection({
        ...stepsSelection,
        [id]: checked
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepsSelection, forecast?.checklist]
  );

  const handleSettingsClick = () => {
    // handleClose();
    openSidePanel?.(undefined, ASSISTANT_TAB.SETTINGS);
  };

  const handleSaveClick = useCallback(async () => {
    try {
      setIsSaving(true);
      await updateForecast?.(
        { checklist: Object.keys(stepsSelection).filter(k => stepsSelection[k]) },
        undefined,
        LOADER_AREAS.appArea
      );
      onClose();
    } catch (e) {
      Logger.error(e as string);
      toastError();
    } finally {
      setIsSaving(false);
    }
  }, [updateForecast, stepsSelection, onClose]);

  const checklistActionNum =
    forecast?.kind === 'budget'
      ? BUDGET_ACTIONS_NUM
      : forecast?.kind === 'scenario' && !forecast.include_unpaid_invoices
      ? SCENARIO_ACTIONS_NUM
      : SCENARIO_WITH_INVOICES_ACTION_NUM;

  return (
    <Sidebar
      open={isOpen}
      data-testid="snapshot-drawer"
      aria-label={intl.formatMessage({
        id: 'predict.update_checklist_steps.aria_label',
        defaultMessage: 'Update checklist steps'
      })}
      size="medium-large"
      onCancel={onClose}
      header={
        <Typography variant="h2" fontWeight="700" mb={0}>
          <FormattedMessage
            id="predict.hi_name_these_steps_may_help"
            defaultMessage="<b>Hi {firstName},</b> these steps may help when creating."
            values={{
              firstName,
              b: (chunks: string) => <span style={{ fontWeight: 700 }}>{chunks}</span>
            }}
          />
        </Typography>
      }
    >
      <Form
        leftSideButtons={
          <Button onClick={onClose}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
        }
        saveButton={
          <Button
            buttonType="primary"
            disabled={isSaveDisabled(forecast.checklist, stepsSelection, isSaving)}
            onClick={handleSaveClick}
          >
            <FormattedMessage id="common.save" defaultMessage="Save" />
          </Button>
        }
        onSubmit={e => e.preventDefault()}
        stickyFooter
        buttonAlignment="right"
        footerPadding={{
          p: 2
        }}
      >
        <div>
          <div style={{ margin: '16px 0' }} className={classNames(styles.text, styles.checkTextSize)}>
            <Typography fontWeight="500" fontSize="16px" mb={0}>
              <FormattedMessage
                id="predict.steps_completed"
                defaultMessage="{completed} of {stepsNum} have been completed"
                values={{ completed: numberOfSelectedItems, stepsNum: checklistActionNum }}
              />
            </Typography>
            <div style={{ marginTop: 10 }} className={styles.progressBarDrawer}>
              <div
                style={{
                  width: numberOfSelectedItems === 0 ? '3%' : `${(numberOfSelectedItems / checklistActionNum) * 100}%`
                }}
                className={styles.completedProgressBar}
              />
            </div>
          </div>
          {checkboxInfos?.map((info: ForecastInfo, index: number) => (
            <div
              key={info.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                // eslint-disable-next-line no-magic-numbers
                marginBottom: index !== checkboxInfos?.length - 1 ? 16 : 0
              }}
            >
              <Checkbox
                onChange={e => handleCheckboxClick(e, info.id)}
                checked={Boolean(stepsSelection?.[info.id])}
                disabled={isSaving}
                aria-label={intl.formatMessage({
                  id: 'predict.select_checklist_item.aria_label',
                  defaultMessage: 'Select checklist item'
                })}
                mr="8px"
                size="large"
                mb={0}
              />
              {info.shouldOpenSettings ? (
                <div
                  data-testid="settings"
                  className={classNames(styles.text, styles.checkTextSize, styles.checkboxTextContainer, {
                    [styles.disabledLink]: isSaving
                  })}
                  style={{ textDecoration: 'underline' }}
                  onClick={handleSettingsClick}
                >
                  {info.label}
                  <div style={{ display: 'flex', marginLeft: 'auto' }}>
                    <UISystemIcon iconName="chevron-right" width={12} height={12} />
                  </div>
                </div>
              ) : (
                <Link
                  to={info.link}
                  className={classNames(styles.text, styles.checkTextSize, styles.checkboxTextContainer, {
                    [styles.disabledLink]: isSaving
                  })}
                  style={{ textDecoration: 'underline' }}
                >
                  {info.label}
                  <div style={{ display: 'flex', marginLeft: 'auto' }}>
                    <UISystemIcon iconName="chevron-right" width={12} height={12} />
                  </div>
                </Link>
              )}
            </div>
          ))}
        </div>
      </Form>
    </Sidebar>
  );
};
